import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL } from '../config';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './component/Header';
import PublicFooter from '../components/layout/PublicFooter';
import { StepIndicator } from '../components/ui';
import { customStyles as selectStyles } from '../components/ui/Select';
import Swal from 'sweetalert2';

const CustomPages = () => {
  const { onboardingId, pageId } = useParams();
  const [page, setPage] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(3);
  const [stepsName, setStepsName] = useState({});
  const [nextStepId, setNextStepId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(API_URL + `/custom-step-pages/${pageId}/${onboardingId}`)
      .then((response) => response.json())
      .then((data) => {
        setPage(data.pages);
        setStepsName(data.steps.stepsName);
        setNextStepId(data.steps.id);
        setCurrentStep(data.steps.currentStep);
        initializeFormValues(data);
      })
      .catch((error) => console.error('Error fetching custom step page data:', error));
  }, [pageId, onboardingId, navigate]);

  const initializeFormValues = (pageData) => {
    const initialValues = {};
    pageData.fields.forEach((field) => { initialValues[field.custom_field.id] = ''; });
    setFormValues(initialValues);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormValues({ ...formValues, [name]: selectedOption });
  };

  const handleDatePickerChange = (date, name) => {
    setFormValues({ ...formValues, [name]: date });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const customFieldData = { page_id: pageId, onboarding_id: onboardingId, customFields: {} };
    const validationErrors = [];
    page.fields.forEach((field) => {
      customFieldData['customFields'][field.custom_field.id] = formValues[field.custom_field.id];
      if (field.custom_field.is_required && !formValues[field.custom_field.id]) {
        validationErrors[field.custom_field.id] = `${field.custom_field.field_name} is required.`;
      }
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      Swal.fire({ icon: 'error', title: 'Validation Errors', html: Object.values(validationErrors).join('<br>'), confirmButtonText: 'OK' });
    } else {
      fetch(API_URL + `/custom-step-pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customFieldData),
      })
        .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
        .then((data) => {
          if (nextStepId) navigate(`/plate-information/${onboardingId}/${nextStepId}`);
          else navigate(`/billing-information/${onboardingId}`);
        })
        .catch((error) => console.error('Error sending data to API:', error));
    }
  };

  if (!page) {
    return (
      <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
        <Header />
        <div className="tw-flex tw-items-center tw-justify-center tw-py-32">
          <div className="tw-text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-400 tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100";

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <Header />

      <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-800 tw-py-10 md:tw-py-14">
        <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <h1 className="tw-text-4xl tw-text-white tw-m-0">{page.page_headline}</h1>
        </div>
      </div>

      <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {stepsName && Object.keys(stepsName).length > 0 && (
          <StepIndicator steps={Object.values(stepsName)} currentStep={currentStep - 1} />
        )}

        <div className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100 tw-p-6 md:tw-p-8">
          <form onSubmit={handleSubmit} className="tw-space-y-5">
            {page.fields.map((field) => (
              <div key={field.id} className="tw-flex tw-flex-col tw-gap-1.5">
                <label className="tw-text-sm tw-font-medium tw-text-gray-700">{field.custom_field.field_name}</label>
                {field.custom_field.field_type === 'Text' && (
                  <input type="text" name={field.custom_field.id} value={formValues[field.custom_field.id]} onChange={handleInputChange} className={inputClasses} placeholder={field.custom_field.field_name} />
                )}
                {field.custom_field.field_type === 'Number' && (
                  <input type="number" name={field.custom_field.id} value={formValues[field.custom_field.id]} onChange={handleInputChange} className={inputClasses} placeholder={field.custom_field.field_name} />
                )}
                {field.custom_field.field_type === 'Dropdown List' && (
                  <Select name={field.custom_field.id} value={formValues[field.custom_field.id]} styles={selectStyles} isMulti={field.custom_field.choice_type === 'multiple'} onChange={(selectedOption) => handleSelectChange(selectedOption, field.custom_field.id)} options={JSON.parse(field.custom_field.dropdown_values).map((option) => ({ value: option, label: option }))} />
                )}
                {field.custom_field.field_type === 'Date' && (
                  <DatePicker selected={formValues[field.custom_field.id]} onChange={(date) => handleDatePickerChange(date, field.custom_field.id)} className={inputClasses} placeholderText={field.custom_field.field_name} />
                )}
                {errors[field.custom_field.id] && <p className="tw-text-xs tw-text-error tw-m-0">{errors[field.custom_field.id]}</p>}
              </div>
            ))}

            <div className="tw-pt-4">
              <button type="submit" className="tw-bg-accent-500 hover:tw-bg-accent-600 tw-text-white tw-px-8 tw-py-3 tw-rounded-lg tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-sm hover:tw-shadow-md tw-flex tw-items-center tw-gap-2">
                Next <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </form>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default CustomPages;
